import { useEffect, useState } from 'react';
import { OptOutTrigger, optOutMarketingEmail } from '@/api/marketingEmail';

/**
 * One checkout load (= one iframe). Footer remounts on PREFERRED landscape
 * rotation and when "Use other payment method" swaps the view-owned Footer for
 * Payment's — keep the successful opt-out across those instances so the link
 * does not reappear after the BE already accepted it. Resets on the next
 * checkout open.
 */
let hasOptedOutThisSession = false;
/** Same remount path must not dismiss an open Do Not Sell modal. */
let isDoNotSellModalOpenThisSession = false;
/**
 * Modal result must outlive DoNotSellModal remounts — otherwise the form
 * returns and a second POST is possible. Latched for the open modal's
 * lifetime; cleared when the shopper closes it.
 */
let doNotSellHasResultThisSession = false;
let doNotSellIsSuccessThisSession = false;
/**
 * In-flight LINK POST shared across Footer remounts (same rationale as GPC).
 * Remounts adopt this promise so settle updates the mounted instance.
 */
let linkOptOutInFlight: Promise<boolean> | null = null;
/** GPC is fire-once per checkout load; remounts must not re-POST. */
let hasAttemptedGpcThisSession = false;
/**
 * In-flight GPC POST shared across Footer remounts. Remounts during the request
 * adopt this promise so success updates the mounted instance (setting
 * `hasAttemptedGpcThisSession` before settle would otherwise skip a retry while
 * only the unmounted instance's local state would have received `onSuccess`).
 */
let gpcOptOutInFlight: Promise<boolean> | null = null;

/** Test-only escape hatch so opt-out assertions start from a clean slate. */
export const __resetMarketingEmailOptOutForTests = (): void => {
  hasOptedOutThisSession = false;
  isDoNotSellModalOpenThisSession = false;
  doNotSellHasResultThisSession = false;
  doNotSellIsSuccessThisSession = false;
  linkOptOutInFlight = null;
  hasAttemptedGpcThisSession = false;
  gpcOptOutInFlight = null;
};

/**
 * Honors the browser Global Privacy Control signal on load (no click, no modal)
 * and keeps the Do Not Sell opted-out flag + modal open/result state across
 * Footer remounts (PREFERRED landscape ↔ portrait, "Use other payment method").
 *
 * BE already gates `showMarketingEmailOptOut` on flag + publisher enablement +
 * US tax country (ACDEV-29992).
 */
export const useGpcMarketingEmailOptOut = (enabled: boolean) => {
  const [hasOptedOut, setHasOptedOut] = useState(hasOptedOutThisSession);
  const [isDoNotSellOpen, setIsDoNotSellOpen] = useState(isDoNotSellModalOpenThisSession);
  const [doNotSellHasResult, setDoNotSellHasResult] = useState(doNotSellHasResultThisSession);
  const [doNotSellIsSuccess, setDoNotSellIsSuccess] = useState(doNotSellIsSuccessThisSession);
  const [doNotSellIsPending, setDoNotSellIsPending] = useState(linkOptOutInFlight !== null);

  const markOptedOut = () => {
    hasOptedOutThisSession = true;
    setHasOptedOut(true);
  };

  const openDoNotSellModal = () => {
    isDoNotSellModalOpenThisSession = true;
    setIsDoNotSellOpen(true);
  };

  const closeDoNotSellModal = () => {
    isDoNotSellModalOpenThisSession = false;
    doNotSellHasResultThisSession = false;
    doNotSellIsSuccessThisSession = false;
    setIsDoNotSellOpen(false);
    setDoNotSellHasResult(false);
    setDoNotSellIsSuccess(false);
  };

  const applyLinkOptOutResult = (ok: boolean) => {
    // Success always sticks for the checkout load (hides the link). Result UI
    // only latches while the modal is still open — closing mid-flight must not
    // leave a stale result for the next open.
    if (ok) markOptedOut();
    if (!isDoNotSellModalOpenThisSession) return;
    doNotSellHasResultThisSession = true;
    doNotSellIsSuccessThisSession = ok;
    setDoNotSellHasResult(true);
    setDoNotSellIsSuccess(ok);
  };

  const submitDoNotSellOptOut = () => {
    if (linkOptOutInFlight) return;

    setDoNotSellIsPending(true);
    // Plain fetch (not useMutation): survives Footer remount / QueryClient churn
    // so remounted instances can still adopt the same in-flight result.
    linkOptOutInFlight = optOutMarketingEmail(OptOutTrigger.LINK)
      .then((response) => Boolean(response?.success))
      .catch(() => false)
      .finally(() => {
        linkOptOutInFlight = null;
      });

    void linkOptOutInFlight.then((ok) => {
      setDoNotSellIsPending(false);
      applyLinkOptOutResult(ok);
    });
  };

  // Remount during an in-flight LINK POST: adopt the shared promise so settle
  // still drives the mounted modal (pending → result) without a second POST.
  useEffect(() => {
    if (!linkOptOutInFlight) return;

    let cancelled = false;
    setDoNotSellIsPending(true);

    void linkOptOutInFlight.then((ok) => {
      if (cancelled) return;
      setDoNotSellIsPending(false);
      applyLinkOptOutResult(ok);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adopt once per mount while in flight
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (hasOptedOutThisSession) {
      setHasOptedOut(true);
      return;
    }
    if (navigator.globalPrivacyControl !== true) return;

    let cancelled = false;

    const adoptResult = (ok: boolean) => {
      if (cancelled || !ok) return;
      setHasOptedOut(true);
    };

    if (gpcOptOutInFlight) {
      void gpcOptOutInFlight.then(adoptResult);
      return () => {
        cancelled = true;
      };
    }

    if (hasAttemptedGpcThisSession) return;

    hasAttemptedGpcThisSession = true;
    // Plain fetch (not useMutation): survives Footer remount / QueryClient churn
    // so remounted instances can still adopt the same in-flight result.
    gpcOptOutInFlight = optOutMarketingEmail(OptOutTrigger.GPC)
      .then((response) => {
        const ok = Boolean(response?.success);
        if (ok) hasOptedOutThisSession = true;
        return ok;
      })
      .catch(() => false)
      .finally(() => {
        gpcOptOutInFlight = null;
      });

    void gpcOptOutInFlight.then(adoptResult);
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return {
    hasOptedOut,
    markOptedOut,
    isDoNotSellOpen,
    openDoNotSellModal,
    closeDoNotSellModal,
    doNotSellHasResult,
    doNotSellIsSuccess,
    doNotSellIsPending,
    submitDoNotSellOptOut,
  };
};
